/**
 * Pure reorder logic for the Object Explorer pinned region.
 *
 * Why this file exists: the `onDragEnd` callback in
 * `TreeNode.tsx > PinnedChildrenSortable` is non-trivial — it derives the
 * permutation payload from the rendered children (to stay consistent with
 * what the user saw and dragged), handles defensive empty/missing-pinRef
 * cases, fires analytics only on IPC success, and surfaces a toast on
 * rejection (multi-window race per F8). The e2e for drag was routed around
 * dnd-kit's headless flakiness, so this entry point had zero coverage at any
 * layer (review finding #6). Extracting the side-effect logic here makes
 * it unit-testable without mounting React.
 */

import type { PinnedObjectRef } from '../../../../types/electron';
import { pinRefKey } from './pinRef';

/**
 * Pure permutation step: given the rendered pinned children (each carrying a
 * `pinRef`) and a from/to id pair, return the new ref order. Returns `null`
 * when the move is invalid (unknown id, missing pinRef on any child).
 */
export function computeReorderedRefs(
  pinnedChildren: ReadonlyArray<{ id: string; pinRef?: PinnedObjectRef }>,
  fromId: string,
  toId: string,
): PinnedObjectRef[] | null {
  if (fromId === toId) return null;
  const ids = pinnedChildren.map((c) => c.id);
  const fromIdx = ids.indexOf(fromId);
  const toIdx = ids.indexOf(toId);
  if (fromIdx < 0 || toIdx < 0) return null;
  const refs = pinnedChildren
    .map((c) => c.pinRef)
    .filter((r): r is PinnedObjectRef => !!r);
  if (refs.length !== pinnedChildren.length) return null;
  const next = refs.slice();
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
}

/**
 * I1 — stable-merge a VISIBLE-subset reordering into the FULL authoritative
 * pin list. As of Task 15 every pin kind (including workspace pins —
 * tableView/query/notebook) renders in the tree, so the visible set normally
 * equals the authoritative set and this merge is effectively a pass-through.
 * It is retained as defense: should any pin kind ever be filtered out of the
 * rendered tree again, a drag-derived `orderedVisible` would omit those hidden
 * pins, and sending that reduced array verbatim would make the backend
 * correctly reject it (not a permutation of the authoritative set). This merge
 * keeps every hidden pin at its original index and fills the remaining slots
 * with the visible pins in their new relative order.
 *
 * Defensive edges: a visible ref the authoritative list doesn't know (stale
 * view) is appended rather than dropped — the backend permutation check will
 * reject the write and the F8 recovery path re-syncs from disk.
 */
export function mergeVisibleReorder(
  authoritative: readonly PinnedObjectRef[],
  orderedVisible: readonly PinnedObjectRef[],
): PinnedObjectRef[] {
  const visibleKeys = new Set(orderedVisible.map(pinRefKey));
  const queue = [...orderedVisible];
  const merged = authoritative.map((r) => {
    if (!visibleKeys.has(pinRefKey(r))) return r; // hidden — keep original index
    return queue.shift() ?? r; // visible slot — next in the new relative order
  });
  // Leftovers = visible refs missing from the authoritative list (stale view).
  return queue.length === 0 ? merged : [...merged, ...queue];
}

export type ReorderToast = {
  warning: (opts: { title: string; description: string; testId?: string }) => void;
};
/**
 * Subset of the project's `TrackFunction` (see `core/analytics/analyticsTypes.ts`).
 * Constrained to scalar values to match the project-wide Umami convention.
 */
export type ReorderAnalytics = {
  track: (name: string, props?: Record<string, string | number | boolean>) => void;
};
export type ReorderStore = {
  reorder: (connectionId: string, ordered: PinnedObjectRef[]) => Promise<void>;
};

/**
 * Drive the reorder side effects: invoke the store, fire analytics on success,
 * surface a toast on rejection. Designed so the caller can pass:
 *   - the live `usePinnedObjectsStore.getState()` in production,
 *   - a stub in unit tests,
 * without `onDragEnd` having to know about React/Zustand internals.
 *
 * Resolves after the store's reorder promise settles; never throws (toast +
 * console.warn surface rejections instead of unwinding the drag callback).
 */
export async function applyPinnedReorder(args: {
  connectionId: string;
  ordered: PinnedObjectRef[];
  store: ReorderStore;
  analytics: ReorderAnalytics;
  toast: ReorderToast;
}): Promise<void> {
  const { connectionId, ordered, store, analytics, toast } = args;
  try {
    await store.reorder(connectionId, ordered);
    analytics.track('feature:object_explorer_pin_reorder', { count: ordered.length });
  } catch (err) {
    toast.warning({
      title: 'Pinned reorder reverted',
      description: 'The pinned list changed in another window — your reorder was undone. Try again with the fresh order.',
      testId: 'toast-pinned-reorder-reverted',
    });
    console.warn('[pinning] reorder failed:', err);
  }
}
